/**
 * Settings Selectors
 * NgRx selectors for settings state
 */
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { SettingsState } from './settings.reducer';
import { MemberStatus, UserRole } from '../models/settings.models';

export const selectSettingsState = createFeatureSelector<SettingsState>('settings');

// Basic Selectors
export const selectUserProfile = createSelector(
  selectSettingsState,
  (state) => state.userProfile
);

export const selectOrganization = createSelector(
  selectSettingsState,
  (state) => state.organization
);

export const selectTeamMembers = createSelector(
  selectSettingsState,
  (state) => state.teamMembers
);

export const selectTeamMembersTotal = createSelector(
  selectSettingsState,
  (state) => state.teamMembersTotal
);

export const selectTeamMembersQuery = createSelector(
  selectSettingsState,
  (state) => state.teamMembersQuery
);

export const selectTeamMembersPage = createSelector(
  selectTeamMembersQuery,
  (query) => query.page
);

export const selectTeamMembersPageSize = createSelector(
  selectTeamMembersQuery,
  (query) => query.pageSize
);

export const selectNotificationPreferences = createSelector(
  selectSettingsState,
  (state) => state.notificationPreferences
);

export const selectLoading = createSelector(
  selectSettingsState,
  (state) => state.loading
);

export const selectTeamMembersLoading = createSelector(
  selectSettingsState,
  (state) => state.teamMembersLoading
);

export const selectSaving = createSelector(
  selectSettingsState,
  (state) => state.saving
);

export const selectUploading = createSelector(
  selectSettingsState,
  (state) => state.uploading
);

export const selectInviting = createSelector(
  selectSettingsState,
  (state) => state.inviting
);

export const selectError = createSelector(
  selectSettingsState,
  (state) => state.error
);

// Computed Selectors - User Profile
export const selectUserFullName = createSelector(
  selectUserProfile,
  (profile) => profile ? `${profile.first_name} ${profile.last_name}`.trim() : ''
);

export const selectUserRole = createSelector(
  selectUserProfile,
  (profile) => profile?.role || UserRole.VIEWER
);

export const selectIs2FAEnabled = createSelector(
  selectUserProfile,
  (profile) => !!(profile?.has_2fa_enabled ?? profile?.two_factor_enabled)
);

export const selectClient2FAAvailable = createSelector(
  selectUserProfile,
  (profile) => profile?.client_2fa_available
);

export const selectClient2FAEnforce = createSelector(
  selectUserProfile,
  (profile) => !!profile?.client_enforce_2fa
);

export const selectUserAvatarUrl = createSelector(
  selectUserProfile,
  (profile) => profile?.avatar_url
);

// Computed Selectors - Organization
export const selectOrganizationName = createSelector(
  selectOrganization,
  (org) => org?.name || ''
);

export const selectOrganizationLogoUrl = createSelector(
  selectOrganization,
  (org) => org?.logo_url
);

export const selectIsOrganizationOwner = createSelector(
  selectUserRole,
  (role) => role === UserRole.OWNER
);

export const selectCanManageTeam = createSelector(
  selectUserRole,
  (role) => role === UserRole.OWNER || role === UserRole.ADMIN
);

// Computed Selectors - Team Members
export const selectTeamMemberCount = createSelector(
  selectTeamMembers,
  (members) => members.length
);

export const selectActiveTeamMembers = createSelector(
  selectTeamMembers,
  (members) => members.filter(m => m.status === MemberStatus.ACTIVE)
);

export const selectInvitedTeamMembers = createSelector(
  selectTeamMembers,
  (members) => members.filter(m => m.status === MemberStatus.INVITED)
);

export const selectActiveTeamMemberCount = createSelector(
  selectActiveTeamMembers,
  (members) => members.length
);

export const selectInvitedTeamMemberCount = createSelector(
  selectInvitedTeamMembers,
  (members) => members.length
);

export const selectTeamMembersByRole = createSelector(
  selectTeamMembers,
  (members) => {
    const byRole: Record<string, number> = {
      owner: 0,
      admin: 0,
      member: 0,
      viewer: 0
    };
    members.forEach(m => {
      byRole[m.role] = (byRole[m.role] || 0) + 1;
    });
    return byRole;
  }
);

// Computed Selectors - Notification Preferences
export const selectEmailNotifications = createSelector(
  selectNotificationPreferences,
  (prefs) => prefs?.email_notifications
);

export const selectWebhookNotifications = createSelector(
  selectNotificationPreferences,
  (prefs) => prefs?.webhook_notifications
);

export const selectSlackIntegration = createSelector(
  selectNotificationPreferences,
  (prefs) => prefs?.slack_integration
);

export const selectIsSlackConnected = createSelector(
  selectSlackIntegration,
  (slack) => slack?.enabled || false
);

